import React from "react";
import { useFieldArray } from "react-hook-form";
import Octicon from "react-octicon";
import {
  materials,
  transport,
  energy,
  ENERGY_INUSE_FACTOR,
  categories,
} from "./factors";

const categoryMap = categories.reduce((collected, categoryParts) => {
  const [first, second, third, fourth] = categoryParts;
  if (!(first in collected)) {
    collected[first] = {};
  }
  if (!(second in collected[first])) {
    collected[first][second] = {};
  }
  if (!(third in collected[first][second])) {
    collected[first][second][third] = [];
  }
  if (!collected[first][second][third].includes(fourth)) {
    collected[first][second][third].push(fourth);
  }
  return collected;
}, {});

const metaSection = ({ register, watch }) => {
  const category = watch(`details.category`);
  const subCategory = watch(`details.subCategory`);
  const type = watch(`details.type`);

  return {
    fields: [],
    component: (
      <ul className="pl-0" style={{ listStyle: "none" }}>
        <li className="my-4">
          <input
            className="form-control"
            {...register("details.name", { required: true })}
          />
        </li>
        <li className="my-4">
          Company name
          <input
            className="form-control"
            {...register("details.company_name")}
            disabled
          />
        </li>
        <li className="my-4">
          Data Accuracy Rating:
          <select className="form-control" {...register(`details.rag`)}>
            <option>Green: EPD and/or custom emission factors</option>
            <option>Amber: Some custom emission factors provided</option>
            <option>Red: Default emission factors used</option>
          </select>
        </li>
        <li className="my-4">
          Total Mass (kg):
          <input
            type="number"
            className="form-control"
            {...register("details.mass")}
          />
        </li>
        <li className="my-4">
          Asset category:
          <select className="form-control" {...register(`details.category`)}>
            {Object.keys(categoryMap).map((category) => (
              <option>{category}</option>
            ))}
          </select>
          <select className="form-control" {...register(`details.subCategory`)}>
            <option>-</option>
            {category in categoryMap &&
              Object.keys(categoryMap[category]).map((subCategory) => (
                <option>{subCategory}</option>
              ))}
          </select>
          <select className="form-control" {...register(`details.type`)}>
            <option>-</option>
            {category in categoryMap &&
              subCategory in categoryMap[category] &&
              Object.keys(categoryMap[category][subCategory]).map((type) => (
                <option>{type}</option>
              ))}
          </select>
          <select className="form-control" {...register(`details.unit`)}>
            <option>-</option>
            {category in categoryMap &&
              subCategory in categoryMap[category] &&
              type in categoryMap[category][subCategory] &&
              categoryMap[category][subCategory][type].map((unit) => (
                <option>{unit}</option>
              ))}
          </select>
        </li>
      </ul>
    ),
  };
};

export function RedStar() {
  return <span style={{ color: "red" }}>*</span>;
}

function FormHeaders({ title, append, isDraft }) {
  return (
    <h5 className="font-weight-bold my-5 pt-5 border-top">
      {title}
      {isDraft && (
        <button
          type="button"
          className="float-right btn btn-sm btn-primary"
          onClick={() => append({})}
        >
          Add new entry
          <Octicon className="ml-2 pt-1" name="plus" />
        </button>
      )}
    </h5>
  );
}

function DeleteButton({ remove, index }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-danger"
      onClick={() => remove(index)}
    >
      <Octicon name="trashcan" />
    </button>
  );
}

const tableHeaderStyles = "font-weight-normal pb-0";

const a1Section = ({ control, register, watch, isDraft }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "details.moduleSubmissions.a1",
  });

  return {
    fields,
    component: (
      <>
        <FormHeaders
          title="A1 Manufacture of the asset: materials"
          append={append}
          isDraft={isDraft}
        />
        <div className="table-responsive">
          <table className="table table-borderless">
            {fields.map((item, index) => {
              const selectedMaterial = watch(
                `details.moduleSubmissions.a1.${index}.material`,
              );
              const mass = watch(`details.moduleSubmissions.a1.${index}.mass`);
              const material = materials?.find(
                ({ name }) => name === selectedMaterial,
              );
              const customEmissionFactor = watch(
                `details.moduleSubmissions.a1.${index}.supplier_emission_factor`,
              );
              const defaultEmissionFactor = material?.factor;
              const calculatedkgCO2e =
                (Number.isFinite(customEmissionFactor)
                  ? customEmissionFactor
                  : defaultEmissionFactor) * mass;
              return (
                <>
                  <tr>
                    <th className={tableHeaderStyles}>
                      Material <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Mass (kg) <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Custom Emission Factor (optional)
                    </th>
                    <th className={tableHeaderStyles}>Comment</th>
                  </tr>
                  <tr key={item.id}>
                    <td>
                      <select
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a1.${index}.material`,
                          { required: true },
                        )}
                      >
                        {materials.map((option) => (
                          <option>{option.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a1.${index}.mass`,
                          {
                            setValueAs: parseFloat,
                            required: true,
                          },
                        )}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a1.${index}.supplier_emission_factor`,
                          {
                            setValueAs: parseFloat,
                          },
                        )}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a1.${index}.comment`,
                        )}
                      />
                    </td>
                    {isDraft && (
                      <td>
                        <DeleteButton remove={remove} index={index} />
                      </td>
                    )}{" "}
                  </tr>
                  <tr>
                    <td>
                      Default Emission Factor:
                      <input
                        type="number"
                        className="form-control"
                        disabled
                        value={defaultEmissionFactor}
                      />
                    </td>
                    <td>
                      Calculated kgCO2e:
                      <input
                        type="number"
                        className="form-control"
                        disabled
                        value={calculatedkgCO2e}
                      />
                    </td>
                  </tr>
                </>
              );
            })}
          </table>
        </div>
      </>
    ),
  };
};

const a2Section = ({ control, register, watch, a1fields, isDraft }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "details.moduleSubmissions.a2",
  });

  return {
    fields,
    component: (
      <>
        <FormHeaders
          title="A2 Manufacture of the asset: transport"
          append={append}
          isDraft={isDraft}
        />
        <div className="table-responsive">
          <table className="table table-borderless">
            {fields?.map((item, index) => {
              const selectedMode = watch(
                `details.moduleSubmissions.a2.${index}.mode`,
              );
              const selectedEntry = watch(
                `details.moduleSubmissions.a2.${index}.item`,
              );
              const mass = watch(
                `details.moduleSubmissions.a1.${selectedEntry}.mass`,
              );
              const mode = transport.find(({ name }) => name === selectedMode);
              const defaultEmissionFactor = mode?.factor;

              const distance = watch(
                `details.moduleSubmissions.a2.${index}.distance`,
              );
              let calculatedkgCO2e = "";
              if (defaultEmissionFactor && mass && distance)
                calculatedkgCO2e = defaultEmissionFactor * mass * distance;

              return (
                <>
                  <tr>
                    <th className={tableHeaderStyles}>
                      Leg <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>Material</th>
                    <th className={tableHeaderStyles}>
                      Mode <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Distance (km) <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>Comment</th>
                  </tr>
                  <tr>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a2.${index}.leg`,
                          { required: true },
                        )}
                      />
                    </td>
                    <td>
                      <select
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a2.${index}.item`,
                        )}
                      >
                        {a1fields.map((a1item, a1index) => {
                          const optionMaterial = watch(
                            `details.moduleSubmissions.a1.${a1index}.material`,
                          );
                          const optionMass = watch(
                            `details.moduleSubmissions.a1.${a1index}.mass`,
                          );

                          return (
                            <option value={a1index}>
                              {optionMaterial}: {optionMass}kg
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a2.${index}.mode`,
                          { required: true },
                        )}
                      >
                        {transport.map(({ name }) => (
                          <option>{name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a2.${index}.distance`,
                          {
                            setValueAs: parseFloat,
                            required: true,
                          },
                        )}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a2.${index}.comment`,
                        )}
                      />
                    </td>
                    {isDraft && (
                      <td>
                        <DeleteButton remove={remove} index={index} />
                      </td>
                    )}
                  </tr>
                  <tr>
                    <td>
                      Default Emission Factor:
                      <input
                        type="number"
                        disabled
                        value={defaultEmissionFactor}
                        className="form-control"
                      />
                    </td>
                    <td>
                      Calculated kgCO2e:
                      <input
                        type="number"
                        disabled
                        value={calculatedkgCO2e}
                        className="form-control"
                      />
                    </td>
                  </tr>
                </>
              );
            })}
          </table>
        </div>
      </>
    ),
  };
};

const a3Section = ({ control, register, watch, isDraft }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "details.moduleSubmissions.a3",
  });

  return {
    fields,
    component: (
      <>
        <FormHeaders
          title="A3 Manufacture of the asset: energy"
          append={append}
          isDraft={isDraft}
        />
        <div className="table-responsive">
          <table className="table table-borderless">
            {fields.map((item, index) => {
              const selectedSource = watch(
                `details.moduleSubmissions.a3.${index}.source`,
              );
              const source = energy?.find(
                ({ name }) => name === selectedSource,
              );
              const customEmissionFactor = watch(
                `details.moduleSubmissions.a3.${index}.supplier_emission_factor`,
              );
              const defaultEmissionFactor = source?.factor;
              const calculationFactor = Number.isFinite(customEmissionFactor)
                ? customEmissionFactor
                : defaultEmissionFactor;
              const value = watch(
                `details.moduleSubmissions.a3.${index}.value`,
              );
              let unit = "";
              if (source) unit = `(${source.unit})`;

              return (
                <>
                  <tr>
                    <th className={tableHeaderStyles}>
                      Source <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Amount {unit} <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Custom Emission Factor (optional)
                    </th>
                  </tr>
                  <tr>
                    <td>
                      <select
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a3.${index}.source`,
                          { required: true },
                        )}
                      >
                        {energy.map(({ name }) => (
                          <option>{name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a3.${index}.value`,
                          {
                            required: true,
                            setValueAs: parseFloat,
                          },
                        )}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a3.${index}.supplier_emission_factor`,
                          {
                            required: false,
                            setValueAs: parseFloat,
                          },
                        )}
                      />
                    </td>
                    {isDraft && (
                      <td>
                        <DeleteButton remove={remove} index={index} />
                      </td>
                    )}
                  </tr>
                  <tr>
                    <td>
                      Default Emission Factor:
                      <input
                        type="number"
                        disabled
                        value={defaultEmissionFactor}
                        className="form-control"
                      />
                    </td>
                    <td>
                      Calculated kgCO2e:
                      <input
                        type="number"
                        disabled
                        value={
                          isNaN(calculationFactor * value)
                            ? 0
                            : calculationFactor * value
                        }
                        className="form-control"
                      />
                    </td>
                  </tr>
                </>
              );
            })}
          </table>
        </div>
      </>
    ),
  };
};

const a4Section = ({ control, register, watch, isDraft }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "details.moduleSubmissions.a4",
  });

  return {
    fields,
    component: (
      <>
        <FormHeaders
          title="A4 Delivery to site"
          append={append}
          isDraft={isDraft}
        />
        <div className="table-responsive">
          <table className="table table-borderless">
            {fields?.map((item, index) => {
              const selectedMode = watch(
                `details.moduleSubmissions.a4.${index}.mode`,
              );
              const distance = watch(
                `details.moduleSubmissions.a4.${index}.distance`,
              );
              const mode = transport.find(({ name }) => name === selectedMode);
              const defaultEmissionFactor = mode?.factor;
              const mass = watch(`details.mass`);
              return (
                <>
                  <tr>
                    <th className={tableHeaderStyles}>
                      Leg <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Mode <RedStar />
                    </th>
                    <th className={tableHeaderStyles}>
                      Distance (km) <RedStar />
                    </th>
                  </tr>
                  <tr>
                    <td>
                      <input
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a4.${index}.leg`,
                          { required: true },
                        )}
                      />
                    </td>
                    <td>
                      <select
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a4.${index}.mode`,
                          { required: true },
                        )}
                      >
                        {transport.map(({ name }) => (
                          <option>{name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        {...register(
                          `details.moduleSubmissions.a4.${index}.distance`,
                          {
                            required: true,
                            setValueAs: parseFloat,
                          },
                        )}
                      />
                    </td>
                    {isDraft && (
                      <td>
                        <DeleteButton remove={remove} index={index} />
                      </td>
                    )}
                  </tr>
                  <tr>
                    <td>
                      Default Emission Factor:
                      <input
                        type="number"
                        disabled
                        value={defaultEmissionFactor}
                        className="form-control"
                      />
                    </td>
                    <td>
                      Calculated kgCO2e:
                      <input
                        type="number"
                        disabled
                        value={
                          isNaN(mass * distance * defaultEmissionFactor)
                            ? 0
                            : mass * distance * defaultEmissionFactor
                        }
                        className="form-control"
                      />
                    </td>
                  </tr>
                </>
              );
            })}
          </table>
        </div>
      </>
    ),
  };
};

const b8Section = ({ register, watch }) => {
  const mass = watch("details.moduleSubmissions.b8.mass");
  const leakRate = watch("details.moduleSubmissions.b8.leak_rate");
  const gasTpe = watch("details.moduleSubmissions.b8.gas_type");
  const gwp = watch("details.moduleSubmissions.b8.gwp");
  const lifetime = watch("details.moduleSubmissions.b8.lifetime");
  const annualLosses = watch("details.moduleSubmissions.b8.annual_losses");
  return {
    fields: [],
    component: (
      <>
        <ul className="pl-0" style={{ listStyle: "none" }}>
          <li className="my-4">
            Mass of insulating gas (kg):
            <input
              className="form-control"
              type="number"
              {...register("details.moduleSubmissions.b8.mass", {
                setValueAs: parseFloat,
              })}
            />
          </li>
          <li className="my-4">
            Annual estimated leak rate (%)
            <input
              className="form-control"
              type="number"
              {...register("details.moduleSubmissions.b8.leak_rate", {
                setValueAs: parseFloat,
              })}
            />
          </li>
          <li className="my-4">
            Gas type (e.g. SF6):
            <input
              className="form-control"
              type="text"
              {...register("details.moduleSubmissions.b8.gas_type", {})}
            />
          </li>
          <li className="my-4">
            Global Warming Potential (kgCO2e/kg):
            <input
              className="form-control"
              type="number"
              {...register("details.moduleSubmissions.b8.gwp", {
                setValueAs: parseFloat,
              })}
            />
          </li>
          <li className="my-4">
            Life of asset in years:
            <input
              type="number"
              className="form-control"
              {...register("details.moduleSubmissions.b8.lifetime", {
                setValueAs: parseFloat,
              })}
            />
          </li>
          <li className="my-4">
            Estimate annual energy loss (MWH)
            <input
              className="form-control"
              type="number"
              {...register("details.moduleSubmissions.b8.annual_losses", {
                setValueAs: parseFloat,
              })}
            />
          </li>
        </ul>
        <table>
          <tr>
            <td>
              Annual Leak Mass (kg)
              <input
                disabled
                value={(mass * leakRate) / 100}
                className="form-control"
              />
            </td>
          </tr>
          <br />
          <tr>
            <td>
              Total kgCO2e over lifetime
              <input
                disabled
                value={
                  (((mass * leakRate) / 100) * gwp +
                    annualLosses * ENERGY_INUSE_FACTOR) *
                  lifetime
                }
                className="form-control"
              />
            </td>
          </tr>
        </table>
      </>
    ),
  };
};

export { a1Section, a2Section, a3Section, a4Section, b8Section, metaSection };
